import { supabase } from '@/integrations/supabase/client';
import { Connection } from '@/types/connections';
import { initializeCSRFToken } from '@/utils/securityUtils';
import { logger } from '@/utils/logger';

/**
 * Enhanced Connection Service with improved security validation
 */
export class ConnectionService {
  private static getCSRFToken(): string {
    return initializeCSRFToken();
  }

  static async sendConnectionRequest(senderId: string, receiverId: string): Promise<{
    success: boolean;
    data?: any;
    error?: string;
    cooldownRemainingHours?: number;
  }> {
    try {
      logger.debug("Sending connection request:", { senderId, receiverId });
      
      if (!senderId || !receiverId) {
        return { success: false, error: "Missing sender or receiver ID" };
      }
      
      if (senderId === receiverId) {
        return { success: false, error: "Cannot connect with yourself" };
      }
      
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(senderId) || !uuidRegex.test(receiverId)) {
        return { success: false, error: "Invalid user ID format" };
      }
      
      const { data, error } = await supabase.functions.invoke('create_connection_request', {
        body: {
          sender_id_param: senderId,
          receiver_id_param: receiverId
        },
        headers: {
          'x-csrf-token': this.getCSRFToken()
        }
      });
      
      if (error) {
        logger.error("Edge function error:", error);
        
        if (error.message?.includes('recursion')) {
          throw new Error("Database configuration error. Please try again later.");
        }
        
        throw new Error(error.message || "Failed to send connection request");
      }
      
      logger.debug("Connection request response:", data);
      
      if (data && data.error) {
        if (data.cooldown_remaining_hours) {
          return {
            success: false, 
            error: data.error,
            cooldownRemainingHours: data.cooldown_remaining_hours
          };
        } else {
          return { success: false, error: data.error };
        }
      }
      
      return { success: true, data };
    } catch (err: any) {
      logger.error("Error sending connection request:", err);
      
      let errorMessage = err.message || "Failed to send connection request";
      
      if (err.message?.includes('rate limit')) {
        errorMessage = "Too many connection requests. Please wait before trying again.";
      } else if (err.message?.includes('network')) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }

  static async updateConnectionStatus(connectionId: string, status: 'accepted' | 'rejected'): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    try {
      logger.debug(`Updating connection ${connectionId} to ${status}`);
      
      if (!connectionId || !status) {
        return { success: false, error: "Missing connection ID or status" };
      }
      
      if (!['accepted', 'rejected'].includes(status)) {
        return { success: false, error: "Invalid status. Must be 'accepted' or 'rejected'" };
      }
      
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(connectionId)) {
        return { success: false, error: "Invalid connection ID format" };
      }
      
      const { data, error } = await supabase.functions.invoke('update_connection_status', {
        body: {
          connection_id_param: connectionId,
          status_param: status
        },
        headers: {
          'x-csrf-token': this.getCSRFToken()
        }
      });
      
      if (error) {
        logger.error("Edge function error:", error);
        throw error;
      }
      
      if (data && data.error) {
        throw new Error(data.error);
      }
      
      logger.debug(`Connection ${status} successfully`);
      
      return { success: true, data };
    } catch (err: any) {
      logger.error(`Error ${status === 'accepted' ? 'accepting' : 'rejecting'} connection:`, err);
      
      let errorMessage = err.message || `Could not ${status} the connection request`;
      
      if (err.message?.includes('not found')) {
        errorMessage = "Connection not found or you don't have permission to modify it";
      } else if (err.message?.includes('already')) {
        errorMessage = "Connection has already been processed";
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }

  static async removeConnection(connectionId: string): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    try {
      logger.debug("Removing connection:", connectionId);
      
      if (!connectionId) {
        return { success: false, error: "Missing connection ID" };
      }
      
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(connectionId)) {
        return { success: false, error: "Invalid connection ID format" };
      }
      
      const { data, error } = await supabase.functions.invoke('delete_connection', {
        body: {
          connection_id_param: connectionId
        },
        headers: {
          'x-csrf-token': this.getCSRFToken()
        }
      });
      
      if (error) {
        logger.error("Edge function error:", error);
        throw error;
      }
      
      if (data && data.error) {
        throw new Error(data.error);
      }
      
      logger.debug("Connection removed successfully");
      
      return { success: true, data };
    } catch (err: any) {
      logger.error("Error removing connection:", err);
      
      let errorMessage = err.message || "Could not remove the connection";
      
      if (err.message?.includes('not found')) {
        errorMessage = "Connection not found or already removed";
      } else if (err.message?.includes('permission')) {
        errorMessage = "You don't have permission to remove this connection";
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }
}
